import { useState } from 'react';
import { Modal } from '../ui/Modal';
import { Button } from '../ui/Button';
import { Alert } from '../ui/Alert';
import { Badge } from '../ui/Badge';
import { Sparkles, Loader2, Send, Plus, Clock } from 'lucide-react';
import { List, ListItem } from '@/types/lists';
import { Goal } from '@/types/goals';
import { aiService } from '@/services/aiService';
import { personaService } from '@/services/personaService';
import { useListsStore } from '@/store/listsStore';
import { cn } from '@/utils/cn';

interface ItemCreationFormProps {
  isOpen: boolean;
  onClose: () => void;
  list: List;
  linkedGoal: Goal;
  existingItems: (ListItem & { completed?: boolean })[];
}

interface SuggestedItemPreview {
  content: string;
  description?: string;
  estimatedMinutes?: number;
  difficulty?: 'easy' | 'medium' | 'hard';
  category?: string;
  priority: number;
  selected: boolean;
}

export function ItemCreationForm({
  isOpen,
  onClose,
  list,
  linkedGoal,
  existingItems
}: ItemCreationFormProps) {
  const [userInput, setUserInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [suggestedItems, setSuggestedItems] = useState<SuggestedItemPreview[]>([]);
  const [hasGenerated, setHasGenerated] = useState(false);
  const [isAddingItems, setIsAddingItems] = useState(false);
  
  const { addItemsToList } = useListsStore();

  const handleGenerate = async () => {
    if (!aiService.isConfigured()) {
      setError('AI service not configured. Please check your settings.');
      return;
    }

    setIsLoading(true);
    setError(null);
    setSuggestedItems([]);

    try {
      const profile = personaService.getProfile();
      
      const suggestion = await aiService.generateItemSuggestions({
        userInput,
        linkedList: {
          id: list.id,
          title: list.title,
          description: list.description
        },
        linkedGoal: {
          id: linkedGoal.id,
          title: linkedGoal.title,
          description: linkedGoal.description,
          smartCriteria: linkedGoal.smartCriteria
        },
        existingItems: existingItems.map(item => ({
          content: item.content,
          description: item.description,
          completed: item.completed || false
        })),
        userProfile: {
          name: profile.name,
          jobRole: profile.jobRole,
          age: profile.age
        }
      });

      // Convert suggestions to preview format with all selected by default
      const previews: SuggestedItemPreview[] = suggestion.items.map(item => ({
        ...item,
        selected: true
      }));

      setSuggestedItems(previews);
      setHasGenerated(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to generate suggestions');
    } finally {
      setIsLoading(false);
    }
  };

  const handleToggleItem = (index: number) => {
    setSuggestedItems(prev => 
      prev.map((item, i) => 
        i === index ? { ...item, selected: !item.selected } : item
      )
    );
  };

  const handleAddSelectedItems = async () => {
    const selectedItems = suggestedItems.filter(item => item.selected);
    if (selectedItems.length === 0) {
      setError('Please select at least one item to add');
      return;
    }

    setIsAddingItems(true);
    setError(null);

    try {
      // Convert to ListItem format
      const itemsToAdd = selectedItems.map(item => ({
        content: item.content,
        description: item.description,
        estimatedMinutes: item.estimatedMinutes,
        difficulty: item.difficulty,
        category: item.category
      }));

      await addItemsToList(list.id, itemsToAdd);
      
      // Close the modal on success
      handleClose();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to add items');
    } finally {
      setIsAddingItems(false);
    }
  };

  const handleClose = () => {
    setUserInput('');
    setError(null);
    setSuggestedItems([]);
    setHasGenerated(false);
    setIsAddingItems(false);
    onClose();
  };

  const selectedCount = suggestedItems.filter(item => item.selected).length;

  const getDifficultyColor = (difficulty?: string) => {
    switch (difficulty) {
      case 'easy': return 'bg-green-100 text-green-800 border-green-200';
      case 'medium': return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'hard': return 'bg-red-100 text-red-800 border-red-200';
      default: return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      size="lg"
      title="AI Item Generator"
      description={`Generate actionable items for "${list.title}"`}
      scrollBehavior="inside"
    >
      <div className="space-y-6">
        {!hasGenerated ? (
          // Input Phase
          <div className="space-y-4">
            <div className="space-y-2">
              <label className="text-sm font-medium flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-primary" />
                Describe the items you need (optional)
              </label>
              <textarea
                value={userInput}
                onChange={(e) => setUserInput(e.target.value)}
                placeholder="Optional: Describe specific items you want (e.g., 'Create weekly workout routines', 'Break down the research phase'). Leave blank for AI to analyze existing items and suggest complementary ones."
                className="w-full min-h-[100px] px-3 py-2 text-sm border border-input rounded-md bg-background ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50 resize-none"
                disabled={isLoading}
                onKeyDown={(e) => {
                  if (e.key === 'Enter' && e.ctrlKey) {
                    e.preventDefault();
                    handleGenerate();
                  }
                }}
              />
            </div>
            
            <div className="bg-blue-200 border border-blue-200 rounded-lg p-3">
              <p className="text-sm text-blue-700">
                <strong>Context:</strong> AI will create items for "{list.title}" that advance your goal: "{linkedGoal.title}"
              </p>
              <p className="text-xs text-blue-600 mt-1">
                {existingItems.length > 0 
                  ? `${existingItems.length} existing items will be analyzed to suggest complementary tasks and avoid duplicates`
                  : 'AI will suggest initial items to help you get started with this list'
                }
              </p>
            </div>

            {error && (
              <Alert variant="destructive" className="text-sm">
                {error}
              </Alert>
            )}
          </div>
        ) : (
          // Results Phase
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">Suggested Items</h3>
              <div className="text-sm text-muted-foreground">
                {selectedCount} of {suggestedItems.length} selected
              </div>
            </div>

            <div className="space-y-3 max-h-80 overflow-y-auto">
              {suggestedItems.map((item, index) => (
                <div
                  key={index}
                  className={cn(
                    'border rounded-lg p-3 cursor-pointer transition-all',
                    item.selected 
                      ? 'border-blue-300 bg-background' 
                      : 'border-gray-200 bg-background'
                  )}
                  onClick={() => handleToggleItem(index)}
                >
                  <div className="flex items-start gap-3">
                    <input
                      type="checkbox"
                      checked={item.selected}
                      onChange={() => handleToggleItem(index)}
                      className="mt-1 h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="font-medium text-sm">
                        {item.content}
                      </div>
                      {item.description && (
                        <div className="text-xs text-gray-600 mt-1">
                          {item.description}
                        </div>
                      )}
                      <div className="flex items-center gap-2 mt-2">
                        {item.estimatedMinutes && (
                          <Badge variant="outline" className="text-xs flex items-center gap-1">
                            <Clock className="h-3 w-3" />
                            {item.estimatedMinutes}m
                          </Badge>
                        )}
                        {item.difficulty && (
                          <Badge variant="outline" className={cn('text-xs', getDifficultyColor(item.difficulty))}>
                            {item.difficulty}
                          </Badge>
                        )}
                        {item.category && (
                          <Badge variant="outline" className="text-xs">
                            {item.category}
                          </Badge>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              ))}
            </div>

            {error && (
              <Alert variant="destructive" className="text-sm">
                {error}
              </Alert>
            )}
          </div>
        )}

        {/* Actions */}
        <div className="flex justify-end gap-3 pt-4">
          <Button
            type="button"
            variant="outline"
            onClick={handleClose}
            disabled={isLoading || isAddingItems}
          >
            Cancel
          </Button>
          
          {!hasGenerated ? (
            <Button
              type="button"
              onClick={handleGenerate}
              disabled={isLoading}
              className="gap-2"
            >
              {isLoading ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Generating...
                </>
              ) : (
                <>
                  <Send className="h-4 w-4" />
                  {userInput.trim() ? 'Generate Items' : 'Analyze & Suggest Items'}
                </>
              )}
            </Button>
          ) : (
            <Button
              type="button"
              onClick={handleAddSelectedItems}
              disabled={isAddingItems || selectedCount === 0}
              className="gap-2"
            >
              {isAddingItems ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Adding...
                </>
              ) : (
                <>
                  <Plus className="h-4 w-4" />
                  Add {selectedCount} Item{selectedCount !== 1 ? 's' : ''}
                </>
              )}
            </Button>
          )}
        </div>
      </div>
    </Modal>
  );
}